import produce from "immer";
import { useEffect, useState } from "react";
import { createUseStyles } from "react-jss";
import Selector from "./Selector";

const useStyles = createUseStyles(theme => ({
  "@keyframes fadein": {
    from: { opacity: 0 },
    to: { opacity: 1 },
  },
  container: {
    width: 516,
    background: theme.colorDarkGrey,
    position: "absolute",
    zIndex: 1000,
    boxShadow: "2px 0px 16px rgba(0, 0, 0, 0.27)",
    "border-radius": 3,
    left: "calc( 50% - 328px)",
    top: "8vh",
    padding: "41px 70px",
    color: "white",
    overflow: "auto",
    animation: "$fadein ease 0.2s",
    "& h2": {
      color: theme.colorSecondary,
      marginTop: 0,
    },
    "& button": {
      background: "#575757",
      color: "white",
      outline: "none",
      border: "none",
      "border-radius": 3,
      padding: "4px 15px",
      marginLeft: 15,
      cursor: "pointer",
    },
    "& p": {
      color: "#9e9e9e",
    },
  },
  backgroundHider: {
    width: "100%",
    height: "100%",
    position: "fixed",
    top: 0,
    left: 0,
    zIndex: 10,
  },
  buttons: {
    marginTop: 60,
    width: "100%",
    display: "flex",
    flexDirection: "row",
    justifyContent: "flex-end",
  },
  sel: {
    display: "flex",
    "flex-direction": "row",
    height: 36,
    "& span": {
      margin: [7, 10, 0, 0],
    },
  },
  sub: {
    color: "white !important",
    "font-family": "monospace",
    cursor: "pointer",
  },
  text: {
    background: "#575757",
    color: "white",
    outline: "none",
    border: "none",
    "border-radius": 3,
    padding: "4px 6px",
  },
  add: {
    background: "#575757",
    color: "white",
    outline: "none",
    border: "none",
    "border-radius": 3,
    padding: "4px 15px",
    marginLeft: 10,
    cursor: "pointer",
    transition: "background 0.2s ease",
    "&:hover": {
      background: theme.colorSecondary,
    },
  },
}));

// const valid = [
//   "burgess",
//   "touchtour",
//   "skanska",
//   "mayfair",
//   "radisson",
//   "diageo",
//   "editor",
//   "britishairways",
//   "cdw",
//   "mys",
//   "ventura",
//   "propertyjigsaw",
//   "tour",
//   "kk",
//   "edmiston",
//   "northropjohnson",
//   "fraser",
//   "yachts",
//   "yco",
//   "vmarine",
//   "lifearc",
//   "sym",
//   "lyc",
//   "accor",
// ];

const defaultSubdomains = ["tour", "touchtour"];

const PublishModal = ({
  togglePublishPopup,
  toggleTourSettings,
  tour,
  onUpdateTour,
}) => {
  const [open, setOpen] = useState(false);
  const [useSubdomains, setUseSubdomains] = useState(
    !!(tour.data.options?.publish && tour.data.options?.publish.length > 0),
  );
  const [subdomains, setSubdomains] = useState(
    tour.data.options?.publish || defaultSubdomains,
  );
  const [sdField, setSdField] = useState("");
  const classes = useStyles();

  useEffect(() => {
    if (useSubdomains) {
      onUpdateTour(t =>
        t.data.publish !== subdomains
          ? produce(t, draft => {
              draft.data.options.publish = subdomains;
              draft.data.iframeOptions.publish = subdomains;
            })
          : t,
      );
    }
  }, [useSubdomains, subdomains, onUpdateTour]);

  if (!tour.data.options) {
    return (
      <div>
        <div className={classes.container}>
          <h2>
            Please save your tour for the first time before changing this
            setting
          </h2>
          <div className={classes.buttons}>
            <button
              onClick={() => void (togglePublishPopup(), toggleTourSettings())}
            >
              Back
            </button>
            <button onClick={togglePublishPopup}>Close</button>
          </div>
        </div>
        <div className={classes.backgroundHider} onClick={togglePublishPopup} />
      </div>
    );
  }

  let dropdownValue;
  if (!tour.data.options.publish) {
    dropdownValue = "all";
  } else if (tour.data.options.publish.length === 0 && !useSubdomains) {
    dropdownValue = "none";
  } else {
    dropdownValue = "some";
  }

  return (
    <>
      <div className={classes.container}>
        <h2>Publish Tour</h2>
        <div className={classes.sel}>
          <span>Tour is visible on:</span>
          <Selector
            isTeamSelect
            noMaxWidth
            expanded={open}
            toggle={() => setOpen(o => !o)}
            value={dropdownValue}
            options={[
              { label: "All subdomains", value: "all" },
              { label: "No subdomains", value: "none" },
              { label: "Selected subdomains", value: "some" },
            ]}
            onChange={value => {
              switch (value) {
                case "all":
                  setUseSubdomains(false);
                  onUpdateTour(
                    produce(draft => {
                      draft.data.options.publish = null;
                      draft.data.iframeOptions.publish = null;
                    }),
                  );
                  break;
                case "none":
                  setUseSubdomains(false);
                  onUpdateTour(
                    produce(draft => {
                      draft.data.options.publish = [];
                      draft.data.iframeOptions.publish = [];
                    }),
                  );
                  break;
                case "some":
                  setUseSubdomains(true);
                  onUpdateTour(
                    produce(draft => {
                      draft.data.options.publish =
                        subdomains.length > 0 ? subdomains : defaultSubdomains;
                      draft.data.iframeOptions.publish =
                        subdomains.length > 0 ? subdomains : defaultSubdomains;
                    }),
                  );
                  break;
              }
            }}
          />
        </div>
        {useSubdomains && (
          <>
            <h3>Subdomains</h3>
            <p>Set the subdomains through which this tour will be visible</p>
            <p>Click on a subdomain to remove it</p>
            <div>
              <form
                onSubmit={evt => {
                  evt.preventDefault();
                  const text = sdField
                    .replace(/[\u{0080}-\u{FFFF}]/gu, "")
                    .toLowerCase();
                  if (
                    text &&
                    !subdomains.includes(text) //&&
                    // valid.includes(text)
                  ) {
                    setSubdomains(s => [...s, text]);
                    setSdField("");
                  } else {
                    alert("Not a valid subdomain");
                  }
                }}
              >
                <input
                  className={classes.text}
                  type="text"
                  placeholder="Add subdomain"
                  value={sdField}
                  onChange={evt =>
                    setSdField(
                      evt.target.value
                        .replace(/[\u{0080}-\u{FFFF}]/gu, "")
                        .toLowerCase(),
                    )
                  }
                />
                <input className={classes.add} type="submit" value="Add" />
              </form>
              {subdomains.map((subdomain, i) => (
                <p
                  className={classes.sub}
                  key={subdomain}
                  onClick={() =>
                    setSubdomains(s => s.filter((_, index) => i !== index))
                  }
                >
                  {subdomain}
                </p>
              ))}
            </div>
          </>
        )}
        <div className={classes.buttons}>
          <button
            onClick={() => void (togglePublishPopup(), toggleTourSettings())}
          >
            Back
          </button>
          <button onClick={togglePublishPopup}>Close</button>
        </div>
      </div>
      <div className={classes.backgroundHider} onClick={togglePublishPopup} />
    </>
  );
};

export default PublishModal;
